import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ChevronsUpDown, LogOut, Monitor, Moon, ShieldCheck, Sun, Trash2 } from 'lucide-react';
import {
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui';
import { useAuth } from '@/features/auth/useAuth';
import { useDeleteAllChats } from '@/features/chat/api';
import { initials } from '@/lib/format';
import { useTheme, type ThemePreference } from '@/lib/theme';

export function UserMenu({ onNavigate }: { onNavigate?: () => void }) {
  const { me, signOut } = useAuth();
  const navigate = useNavigate();
  const { preference, setPreference } = useTheme();
  const delAll = useDeleteAllChats();
  const [confirmAll, setConfirmAll] = useState(false);
  if (!me) return null;
  const display = me.name || me.email;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-3 rounded-2xl p-2 text-left transition-colors hover:bg-hover data-[state=open]:bg-hover"
            aria-label={`Account menu for ${display}`}
          >
            <span className="grid size-10 shrink-0 place-items-center rounded-full bg-navy-900 text-sm font-semibold text-white dark:bg-royal-500">{initials(display)}</span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-ink">{display}</span>
              {me.name && <span className="block truncate text-xs text-ink-muted">{me.email}</span>}
            </span>
            <ChevronsUpDown className="size-4 shrink-0 text-ink-muted" aria-hidden="true" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="start" className="w-[max(16rem,var(--radix-dropdown-menu-trigger-width))]">
          <DropdownMenuLabel>Theme</DropdownMenuLabel>
          <DropdownMenuRadioGroup value={preference} onValueChange={(v) => setPreference(v as ThemePreference)}>
            <DropdownMenuRadioItem value="light"><Sun className="size-4" /> Light</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="dark"><Moon className="size-4" /> Dark</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="system"><Monitor className="size-4" /> System</DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          {me.role === 'admin' && (
            <DropdownMenuItem
              onSelect={() => {
                onNavigate?.();
                navigate('/admin');
              }}
            >
              <ShieldCheck className="size-4" /> Admin dashboard
            </DropdownMenuItem>
          )}
          <DropdownMenuItem danger onSelect={() => setConfirmAll(true)}>
            <Trash2 className="size-4" /> Delete all chats
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => void signOut()}>
            <LogOut className="size-4" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmDialog
        open={confirmAll}
        onOpenChange={setConfirmAll}
        title="Delete all chats?"
        description="Every conversation and its attachments will be permanently deleted. This cannot be undone."
        confirmLabel="Delete all"
        loading={delAll.isPending}
        onConfirm={() => {
          delAll.mutate();
          setConfirmAll(false);
          navigate('/');
        }}
      />
    </>
  );
}
